import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Users, RefreshCw, Download, Search,
  ChevronLeft, ChevronRight, CheckCircle, AlertCircle,
  X, Clock, Camera, MapPin, Shield,
  MoreVertical, Edit2, TrendingUp, IndianRupee,
  Check, XCircle, Calendar, ChevronDown, ChevronUp,
  ZoomIn,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

const API = API_BASE_URL;

// ── Attendance config
const ATT_CONFIG = {
  FD: { label: "Present", badge: "FD", tw: "bg-[rgba(16,185,129,0.12)] text-[#10b981] border-[rgba(16,185,129,0.28)]", selected: "bg-[rgba(16,185,129,0.2)] text-[#10b981] border-[#10b981]" },
  FN: { label: "Present", badge: "FN", tw: "bg-[rgba(99,102,241,0.12)] text-[#6366f1] border-[rgba(99,102,241,0.28)]", selected: "bg-[rgba(99,102,241,0.2)] text-[#6366f1] border-[#6366f1]" },
  HD: { label: "Half Day", badge: "HD", tw: "bg-[rgba(245,158,11,0.12)] text-[#f59e0b] border-[rgba(245,158,11,0.28)]", selected: "bg-[rgba(245,158,11,0.2)] text-[#f59e0b] border-[#f59e0b]" },
  HN: { label: "Half Day", badge: "HN", tw: "bg-[rgba(251,146,60,0.12)] text-[#fb923c] border-[rgba(251,146,60,0.28)]", selected: "bg-[rgba(251,146,60,0.2)] text-[#fb923c] border-[#fb923c]" },
  DD: { label: "Present", badge: "DD", tw: "bg-[rgba(59,130,246,0.12)] text-[#3b82f6] border-[rgba(59,130,246,0.28)]", selected: "bg-[rgba(59,130,246,0.2)] text-[#3b82f6] border-[#3b82f6]" },
  A: { label: "Absent", badge: "A", tw: "bg-[rgba(239,68,68,0.12)] text-[#ef4444] border-[rgba(239,68,68,0.28)]", selected: "bg-[rgba(239,68,68,0.2)] text-[#ef4444] border-[#ef4444]" },
  "—": { label: "Pending", badge: "—", tw: "bg-[var(--surface)] text-[var(--text-muted)] border-[var(--surface-border)]", selected: "bg-[var(--surface-border)] text-[var(--text-primary)] border-[var(--text-muted)]" },
};

const ATT_OPTIONS = [
  { value: "FD", label: "Full Day (FD)" },
  { value: "FN", label: "Full Night (FN)" },
  { value: "HD", label: "Half Day (HD)" },
  { value: "HN", label: "Half Night (HN)" },
  { value: "DD", label: "Double Duty (DD)" },
  { value: "A", label: "Absent (A)" },
];

const REASON_PRESETS = [
  "Guard was on field duty outside campus",
  "Network/device issue prevented selfie upload",
  "Emergency deployment to another site",
  "Medical leave — documents submitted",
  "Admin verified presence via phone",
  "Casual leave approved",
  "Duty extended from previous shift",
  "Other (custom reason)",
];

const APP_TIME_ZONE = "Asia/Kolkata";

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const text = String(value).trim();
  if (!text) return null;
  const normalized =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text) &&
    !/(Z|[+-]\d{2}:?\d{2})$/i.test(text)
      ? `${text}Z`
      : text;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const fmtDate = (value) => {
  if (!value) return "—";
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? new Date(`${value}T00:00:00.000Z`)
    : parseDateValue(value);
  if (!parsed) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? "UTC" : APP_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

const fmtTime = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: APP_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(parsed);
};

const fmtCheckTime = fmtTime;
const fmtSelfieTime = fmtTime;

const getToday = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value])
  );
  return `${values.year}-${values.month}-${values.day}`;
};

// ─────────────────────────────────────────────
// Attendance Badge
// ─────────────────────────────────────────────
const AttBadge = ({ desc }) => {
  const cfg = ATT_CONFIG[desc] || ATT_CONFIG["—"];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold border ${cfg.tw}`} style={{ fontFamily: FONT_UI }}>
      {cfg.label}
      <span className="font-mono text-[9px] opacity-80">({cfg.badge})</span>
    </span>
  );
};

// ─────────────────────────────────────────────
// Source Badge
// ─────────────────────────────────────────────
const SourceBadge = ({ source }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-extrabold border ${
      source === "Automatic"
        ? "bg-[rgba(99,102,241,0.12)] text-[#6366f1] border-[rgba(99,102,241,0.28)]"
        : "bg-[rgba(251,146,60,0.12)] text-[#fb923c] border-[rgba(251,146,60,0.28)]"
    }`}
    style={{ fontFamily: FONT_UI }}
  >
    {source === "Automatic" ? "⚙ Auto" : source === "Manual" ? "✏ Manual" : "—"}
  </span>
);

// ─────────────────────────────────────────────
// Selfie Count Summary Cell
// ─────────────────────────────────────────────
const SelfieCountCell = ({ row, onOpenViewer }) => {
  const uploaded = row.verified_selfies || 0;
  const pending = row.pending_selfies || 0;
  const inCampus = row.total_scheduled || 0;
  const notUploaded = row.not_uploaded || 0;

  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onOpenViewer(row);
        }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border whitespace-nowrap w-fit ${
          uploaded > 0
            ? "bg-[rgba(16,185,129,0.12)] text-[#10b981] border-[rgba(16,185,129,0.28)]"
            : "bg-[var(--surface)] text-[var(--text-muted)] border-[var(--surface-border)]"
        }`}
        style={{ fontFamily: FONT_UI }}
      >
        <Camera size={10} /> {uploaded} uploaded
      </button>

      <div
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border whitespace-nowrap w-fit ${
          inCampus > 0
            ? "bg-[rgba(59,130,246,0.12)] text-[#3b82f6] border-[rgba(59,130,246,0.28)]"
            : "bg-[var(--surface)] text-[var(--text-muted)] border-[var(--surface-border)]"
        }`}
        style={{ fontFamily: FONT_UI }}
      >
        <MapPin size={10} /> {inCampus} in-campus
      </div>

      {notUploaded > 0 && (
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border bg-[var(--red-faint)] text-[var(--red)] border-[var(--red-border)] whitespace-nowrap w-fit" style={{ fontFamily: FONT_UI }}>
          <XCircle size={10} /> {notUploaded} not uploaded
        </div>
      )}

      {pending > 0 && (
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border bg-[rgba(245,158,11,0.12)] text-[#f59e0b] border-[rgba(245,158,11,0.28)] whitespace-nowrap w-fit" style={{ fontFamily: FONT_UI }}>
          <Clock size={10} /> {pending} pending
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Expandable Row
// ─────────────────────────────────────────────
const ExpandedRow = ({ row, colSpan, onOpenViewer }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    // IMPORTANT: pass both member_id + staff_id
    fetch(
      `${API}/sentinel-attendance/guard-detail?member_id=${encodeURIComponent(
        row.member_id || ""
      )}&staff_id=${row.staff_id}&date=${row.att_date}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setDetail(d);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [row.member_id, row.staff_id, row.att_date]);

  const campusChecks = detail?.campus_checks || [];
  const selfies = detail?.selfies || [];

  const statusClasses = (s) => {
    if (s === "completed" || s === "verified")
      return { dot: "bg-emerald-500", pill: "bg-[rgba(16,185,129,0.12)] text-[#10b981]" };
    if (s === "notified")
      return { dot: "bg-amber-500", pill: "bg-[rgba(245,158,11,0.12)] text-[#f59e0b]" };
    return { dot: "bg-red-500", pill: "bg-[rgba(239,68,68,0.12)] text-[#ef4444]" };
  };

  return (
    <tr>
      <td colSpan={colSpan} className="p-0 border-b-2 border-[var(--accent)]">
        <div className="bg-[var(--accent-faint)] px-6 py-4">
          {loading ? (
            <div className="flex items-center gap-2 text-[var(--text-muted)] text-[13px] py-2" style={{ fontFamily: FONT_UI }}>
              <div className="w-3.5 h-3.5 border-2 border-[var(--surface-border)] border-t-[var(--accent)] rounded-full animate-spin" />
              Loading details…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Campus Check Timeline */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={13} className="text-[var(--accent)]" />
                  <span className="text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide" style={{ fontFamily: FONT_UI }}>
                    In-Campus Checks ({campusChecks.length})
                  </span>
                  {campusChecks.length > 0 && (
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-[rgba(16,185,129,0.12)] text-[#10b981] text-[10px] font-extrabold" style={{ fontFamily: FONT_UI }}>
                      {campusChecks.filter((c) => c.status === "completed" || c.status === "verified").length}/{campusChecks.length} done
                    </span>
                  )}
                </div>

                {campusChecks.length === 0 ? (
                  <div className="px-4 py-3 bg-[var(--card-bg)] rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] text-center" style={{ fontFamily: FONT_UI }}>
                    No campus checks scheduled for this date
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {campusChecks.map((c, i) => {
                      const isDone = c.status === "completed" || c.status === "verified";
                      const sc = statusClasses(c.status);
                      return (
                        <div
                          key={c.id}
                          className={`flex items-start gap-3 px-3 py-2.5 bg-[var(--card-bg)] rounded-xl border ${
                            isDone ? "border-[rgba(16,185,129,0.28)]" : "border-[var(--surface-border)]"
                          }`}
                        >
                          <div className="flex flex-col items-center pt-0.5">
                            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                            {i < campusChecks.length - 1 && <div className="w-px flex-1 min-h-[12px] bg-[var(--divider)] mt-1" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[var(--text-primary)] font-mono text-[13px] font-extrabold">{fmtCheckTime(c.check_time)}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase ${sc.pill}`}>{c.status}</span>
                            </div>
                            {c.remarks && (
                              <div className="text-[var(--text-muted)] text-[11px] mt-0.5" style={{ fontFamily: FONT_UI }}>
                                {c.remarks}
                              </div>
                            )}
                            {c.checked_at && (
                              <div className="text-[#10b981] text-[10px] mt-0.5" style={{ fontFamily: FONT_UI }}>
                                Completed: {fmtCheckTime(c.checked_at)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right: Selfie thumbnails */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Camera size={13} className="text-[var(--accent)]" />
                  <span className="text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide" style={{ fontFamily: FONT_UI }}>
                    Selfies ({selfies.length})
                  </span>
                  {selfies.length > 0 && (
                    <button
                      onClick={() => onOpenViewer(row, selfies)}
                      className="ml-auto px-2 py-0.5 rounded-full bg-[rgba(59,130,246,0.12)] text-[#3b82f6] border border-[rgba(59,130,246,0.28)] text-[10px] font-extrabold"
                      style={{ fontFamily: FONT_UI }}
                    >
                      View All
                    </button>
                  )}
                </div>

                {selfies.length === 0 ? (
                  <div className="px-4 py-3 bg-[var(--card-bg)] rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] text-center" style={{ fontFamily: FONT_UI }}>
                    No selfies uploaded for this date
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selfies.map((s, i) => {
                      const isVerified = s.verify_status === "verified";
                      const isPending = s.verify_status === "pending";
                      return (
                        <div
                          key={s.selfie_pk}
                          onClick={() => onOpenViewer(row, selfies, i)}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 flex-shrink-0 transition-transform hover:scale-105 group ${
                            isVerified ? "border-[#10b981]" : isPending ? "border-[#f59e0b]" : "border-[var(--surface-border)]"
                          }`}
                        >
                          {s.selfie_image_url ? (
                            <img
                              src={s.selfie_image_url}
                              alt={`selfie ${i + 1}`}
                              className="w-16 h-16 object-cover block"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div
                            className="w-16 h-16 bg-[var(--surface)] items-center justify-center"
                            style={{ display: s.selfie_image_url ? "none" : "flex" }}
                          >
                            <Camera size={20} className="text-[var(--text-muted)]" />
                          </div>

                          <div className="absolute bottom-0.5 left-0.5 right-0.5 flex justify-between items-center">
                            <span className="px-1 py-0.5 rounded bg-black/60 text-white text-[8px] font-bold">
                              {s.selfie_type === "duty_start"
                                ? "Login"
                                : s.selfie_type === "duty_end"
                                ? "Logout"
                                : s.selfie_type === "campus"
                                ? "Campus"
                                : "Selfie"}
                            </span>
                            <div
                              className={`w-2 h-2 rounded-full border border-white/50 ${
                                isVerified ? "bg-emerald-500" : isPending ? "bg-amber-500" : "bg-red-500"
                              }`}
                            />
                          </div>

                          <div className="absolute inset-0 bg-black/35 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex">
                            <ZoomIn size={16} className="text-white" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// ─────────────────────────────────────────────
// Selfie Viewer Modal
// ─────────────────────────────────────────────
const SelfieViewerModal = ({ row, selfies: initialSelfies, startIndex = 0, onClose }) => {
  const [selfies, setSelfies] = useState(initialSelfies || []);
  const [current, setCurrent] = useState(startIndex);
  const [loading, setLoading] = useState(!initialSelfies);

  useEffect(() => {
    if (initialSelfies) return;
    setLoading(true);
    const token = getToken();
    // IMPORTANT: pass both member_id + staff_id
    fetch(
      `${API}/sentinel-attendance/guard-detail?member_id=${encodeURIComponent(
        row.member_id || ""
      )}&staff_id=${row.staff_id}&date=${row.att_date}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setSelfies(d.selfies || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [row.member_id, row.staff_id, row.att_date, initialSelfies]);

  const selfie = selfies[current];
  const isVerified = selfie?.verify_status === "verified";
  const isPending = selfie?.verify_status === "pending";

  const prev = () => setCurrent((c) => Math.max(0, c - 1));
  const next = () => setCurrent((c) => Math.min(selfies.length - 1, c + 1));

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9000] bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute top-0 left-0 right-0 px-6 py-4 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
        <div>
          <div className="text-white text-[13px] font-extrabold" style={{ fontFamily: FONT_UI }}>
            {row.guard_name}
          </div>
          <div className="text-white/60 text-[11px]" style={{ fontFamily: FONT_UI }}>
            {fmtDate(row.att_date)} · {row.customer_name}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-white/60 text-[13px]" style={{ fontFamily: FONT_UI }}>
            {current + 1} / {selfies.length}
          </span>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/60 text-[13px] flex items-center gap-3" style={{ fontFamily: FONT_UI }}>
          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          Loading selfies…
        </div>
      ) : selfies.length === 0 ? (
        <div className="text-center text-white/50" style={{ fontFamily: FONT_UI }}>
          <Camera size={48} className="opacity-30 block mx-auto mb-4" />
          <p>No selfies found for this date</p>
        </div>
      ) : (
        <>
          <div className="relative flex items-center justify-center w-full" style={{ maxWidth: "min(600px, 90vw)", maxHeight: "60vh" }}>
            {current > 0 && (
              <button
                onClick={prev}
                className="absolute -left-14 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white z-10 hover:bg-white/20"
              >
                <ChevronLeft size={20} />
              </button>
            )}

            {selfie?.selfie_image_url ? (
              <img
                src={selfie.selfie_image_url}
                alt="selfie"
                className="max-h-[60vh] max-w-full rounded-xl object-contain shadow-2xl"
              />
            ) : (
              <div className="w-64 h-64 bg-white/10 rounded-xl flex items-center justify-center">
                <Camera size={48} className="text-white/40" />
              </div>
            )}

            {current < selfies.length - 1 && (
              <button
                onClick={next}
                className="absolute -right-14 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white z-10 hover:bg-white/20"
              >
                <ChevronRight size={20} />
              </button>
            )}
          </div>

          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-[12px] font-extrabold">
              {selfie?.selfie_type === "duty_start"
                ? "Login Selfie"
                : selfie?.selfie_type === "duty_end"
                ? "Logout Selfie"
                : selfie?.selfie_type === "campus"
                ? "Campus Check"
                : "Selfie"}
              <span
                className={`w-2 h-2 rounded-full ${
                  isVerified ? "bg-emerald-400" : isPending ? "bg-amber-400" : "bg-red-400"
                }`}
              />
              <span className="text-white/70 font-normal">{fmtSelfieTime(selfie?.created_at_utc)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function SentinelAttendance() {
  const skin = useSkin();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [allocations, setAllocations] = useState([]);
  const [allocationId, setAllocationId] = useState("");
  const [activeAlloc, setActiveAlloc] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [search, setSearch] = useState("");
  const [attendanceStatus, setAttendanceStatus] = useState("");
  const [dateView, setDateView] = useState("today"); // today | past

  const [expandedKey, setExpandedKey] = useState(null);
  const [viewer, setViewer] = useState(null); // { row, selfies, startIndex }
  const [overrideRow, setOverrideRow] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);

  const COL_COUNT = 12;
  const totalPages = Math.ceil(total / limit) || 1;

  // ── Load companies
  useEffect(() => {
    const token = getToken();
    fetch(`${API}/sentinel-attendance/companies`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setCompanies(d.data || []);
      })
      .catch(() => {});
  }, []);

  // ── Load allocations when company changes
  useEffect(() => {
    if (!companyId) {
      setAllocations([]);
      setAllocationId("");
      setActiveAlloc(null);
      return;
    }
    const token = getToken();
    fetch(`${API}/sentinel-attendance/allocations?customer_id=${companyId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setAllocations(d.data || []);
          if (d.data?.length) {
            setAllocationId(String(d.data[0].id));
            setActiveAlloc(d.data[0]);
          }
        }
      })
      .catch(() => {});
  }, [companyId]);

  // ── Load dashboard data
  const loadData = useCallback(() => {
    if (!allocationId) return;
    setLoading(true);
    const token = getToken();

    const params = new URLSearchParams({
      allocation_id: allocationId,
      page: String(page),
      limit: String(limit),
    });

    if (search.trim()) params.set("search", search.trim());
    if (attendanceStatus) params.set("attendance_status", attendanceStatus);

    if (dateView === "today") {
      const today = getToday();
      params.set("from_date", today);
      params.set("to_date", today);
    }

    fetch(`${API}/sentinel-attendance/dashboard?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setRows(d.data || []);
          setTotal(d.total || 0);
          setSummary(d.summary || {});
        }
      })
      .catch(() => toast.error("Failed to load attendance"))
      .finally(() => setLoading(false));
  }, [allocationId, page, limit, search, attendanceStatus, dateView]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleExpand = (row) => {
    const key = `${row.staff_id}_${row.att_date}`;
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  const openViewer = (row, selfies = null, startIndex = 0) => {
    setViewer({ row, selfies, startIndex });
  };

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-5 md:p-8 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] m-0" style={{ fontFamily: FONT_HEADING }}>
              Sentinel Attendance
            </h1>
            <p className="text-[var(--text-muted)] text-[13px] mt-1 m-0">
              Real-time guard attendance with selfies & campus checks
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--card-bg)] border border-[var(--surface-border)] text-[var(--text-primary)] text-[13px] font-extrabold hover:bg-[var(--surface)]"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 mb-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Company */}
            <div>
              <label className="block text-[11px] font-extrabold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                Company
              </label>
              <select
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setPage(1);
                  setExpandedKey(null);
                }}
                className="w-full px-3 py-2.5 rounded-xl bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-primary)] text-[13px] font-extrabold outline-none"
              >
                <option value="">Select company…</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.customer_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-[11px] font-extrabold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                Search
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Name / ID…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-primary)] text-[13px] outline-none"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-[11px] font-extrabold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                Status
              </label>
              <select
                value={attendanceStatus}
                onChange={(e) => {
                  setAttendanceStatus(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 rounded-xl bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-primary)] text-[13px] font-extrabold outline-none"
              >
                <option value="">All Status</option>
                <option value="FD">Present (FD)</option>
                <option value="HD">Half Day</option>
                <option value="A">Absent</option>
              </select>
            </div>

            {/* Date View */}
            <div>
              <label className="block text-[11px] font-extrabold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                View
              </label>
              <div className="flex rounded-xl overflow-hidden border border-[var(--surface-border)]">
                <button
                  onClick={() => {
                    setDateView("today");
                    setPage(1);
                  }}
                  className={`flex-1 py-2.5 text-[12px] font-extrabold ${
                    dateView === "today"
                      ? "bg-[var(--btn-bg)] text-[var(--btn-text)]"
                      : "bg-[var(--surface)] text-[var(--text-muted)]"
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    setDateView("past");
                    setPage(1);
                  }}
                  className={`flex-1 py-2.5 text-[12px] font-extrabold ${
                    dateView === "past"
                      ? "bg-[var(--btn-bg)] text-[var(--btn-text)]"
                      : "bg-[var(--surface)] text-[var(--text-muted)]"
                  }`}
                >
                  All Days
                </button>
              </div>
            </div>
          </div>

          {/* Allocations */}
          {companyId && (
            <div className="mt-5">
              <label className="block text-[11px] font-extrabold text-[var(--text-muted)] uppercase tracking-wide mb-2">
                Allocation Period
              </label>
              {allocations.length === 0 ? (
                <div className="p-4 bg-[var(--card-bg)] border border-dashed border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[13px] text-center">
                  No allocations found for this company
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  {allocations.map((alloc) => {
                    const isActive = String(allocationId) === String(alloc.id);
                    return (
                      <button
                        key={alloc.id}
                        onClick={() => {
                          setAllocationId(String(alloc.id));
                          setActiveAlloc(alloc);
                          setPage(1);
                          setExpandedKey(null);
                          setDateView("past");
                        }}
                        className={`px-4 py-2.5 rounded-xl border-[1.5px] text-left flex flex-col items-start gap-0.5 transition-colors ${
                          isActive
                            ? "bg-[var(--btn-bg)] border-[var(--btn-bg)]"
                            : "bg-[var(--card-bg)] border-[var(--surface-border)] hover:border-[var(--accent-border)]"
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wide ${
                              isActive
                                ? "bg-white/20 text-white"
                                : alloc.allocation_type === "period"
                                ? "bg-[rgba(245,158,11,0.12)] text-[#f59e0b]"
                                : "bg-[rgba(99,102,241,0.12)] text-[#6366f1]"
                            }`}
                          >
                            {alloc.allocation_type === "period" ? "Period" : "Month"}
                          </span>
                          {alloc.emp_count > 0 && (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold ${
                                isActive ? "bg-white/15 text-white/85" : "bg-[var(--surface)] text-[var(--text-muted)]"
                              }`}
                            >
                              {alloc.emp_count} staff
                            </span>
                          )}
                        </div>
                        <span className={`text-[13px] font-extrabold ${isActive ? "text-white" : "text-[var(--text-primary)]"}`}>
                          {alloc.label}
                        </span>
                        <span className={`text-[10px] ${isActive ? "text-white/70" : "text-[var(--text-muted)]"}`}>
                          {alloc.shift_mode}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {!companyId && (
          <div className="text-center py-16 px-8 bg-[var(--card-bg)] border-2 border-dashed border-[var(--surface-border)] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <Shield size={48} className="opacity-10 block mx-auto mb-4" />
            <p className="text-[var(--text-muted)] text-[15px] font-extrabold m-0">Select a company to view attendance</p>
            <p className="text-[var(--text-muted)] text-[13px] mt-1.5 m-0">Then pick an allocation period</p>
          </div>
        )}

        {/* Table */}
        {allocationId && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            {rows.length > 0 && !loading && (
              <div className="px-4 py-2 bg-[var(--accent-faint)] border-b border-[var(--divider)] flex items-center gap-2">
                <ChevronDown size={12} className="text-[var(--text-muted)]" />
                <span className="text-[var(--text-muted)] text-[11px]">
                  Click any row to expand campus checks &amp; selfies
                </span>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] border-collapse">
                <thead>
                  <tr style={{ background: "var(--btn-bg)" }}>
                    {[
                      { label: "", w: "36px" },
                      { label: "Employee Details", w: "190px" },
                      { label: "Site / Campus", w: "140px" },
                      { label: "Shift", w: "140px" },
                      { label: "Login Time", w: "95px" },
                      { label: "Logout Time", w: "95px" },
                      { label: "Selfie & Campus", w: "140px" },
                      { label: "Attendance Status", w: "130px" },
                      { label: "Source", w: "85px" },
                      { label: "Payroll Hours", w: "95px" },
                      { label: "Reason", w: "160px" },
                      { label: "Actions", w: "70px" },
                    ].map(({ label, w }) => (
                      <th
                        key={label}
                        className="px-3 py-3 text-left text-white text-[11px] font-extrabold uppercase tracking-wide whitespace-nowrap border-b border-white/10"
                        style={{ minWidth: w }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={COL_COUNT} className="p-12 text-center text-[var(--text-muted)]">
                        <div className="inline-flex items-center gap-3">
                          <div className="w-[18px] h-[18px] border-2 border-[var(--surface-border)] border-t-[var(--accent)] rounded-full animate-spin" />
                          Loading attendance data…
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={COL_COUNT} className="p-12 text-center">
                        <Users size={40} className="opacity-10 block mx-auto mb-3" />
                        <p className="text-[var(--text-muted)] text-[13px] font-extrabold m-0">No records found</p>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    rows.map((row, idx) => {
                      const rowKey = `${row.staff_id}_${row.att_date}`;
                      const isExpanded = expandedKey === rowKey;
                      const isEven = idx % 2 === 0;
                      const isFuture = row.att_date > getToday();

                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            onClick={() => !isFuture && toggleExpand(row)}
                            className={`${
                              isFuture
                                ? "opacity-45 cursor-not-allowed bg-[repeating-linear-gradient(45deg,var(--surface),var(--surface)_3px,var(--surface-border)_3px,var(--surface-border)_5px)]"
                                : "cursor-pointer hover:bg-[var(--accent-faint)]"
                            } ${
                              isExpanded
                                ? "border-b-0 bg-[var(--accent-faint)]"
                                : `border-b border-[var(--divider)] ${isEven ? "" : "bg-[var(--surface)]"}`
                            }`}
                          >
                            {/* Expand toggle */}
                            <td className="pl-3 pr-2 py-3 text-center">
                              <div className={`flex items-center justify-center ${isExpanded ? "text-[var(--accent)]" : "text-[var(--text-muted)]"}`}>
                                {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                              </div>
                            </td>

                            {/* Employee */}
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-[34px] h-[34px] rounded-full bg-[var(--accent-faint)] flex items-center justify-center flex-shrink-0 border-2 border-[var(--surface-border)] overflow-hidden">
                                  {row.selfie_url ? (
                                    <img
                                      src={row.selfie_url}
                                      alt="guard"
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <Shield size={13} className="text-[var(--accent)]" />
                                  )}
                                </div>
                                <div>
                                  <div className="text-[var(--text-primary)] text-[13px] font-extrabold whitespace-nowrap">
                                    {row.guard_name}
                                  </div>
                                  <div className="text-[var(--accent)] font-mono text-[10px] font-extrabold">
                                    {row.application_number}
                                  </div>
                                  {row.role_name && (
                                    <div className="text-[var(--text-muted)] text-[9px]">{row.role_name}</div>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Campus */}
                            <td className="px-3 py-3">
                              <div className="text-[var(--text-primary)] text-[13px] font-extrabold whitespace-nowrap">
                                {row.customer_name}
                              </div>
                              <div className="text-[var(--text-muted)] text-[10px] flex items-center gap-1 mt-0.5">
                                <MapPin size={9} /> {row.campus_name}
                              </div>
                            </td>

                            {/* Shift */}
                            <td className="px-3 py-3">
                              <div className="text-[var(--text-primary)] text-[13px] font-extrabold">{row.shift_mode}</div>
                              <div className="text-[var(--text-muted)] text-[10px]">
                                {row.shift_start_fmt} – {row.shift_end_fmt}
                              </div>
                              <div className="text-[var(--text-muted)] text-[9px] mt-0.5">{fmtDate(row.att_date)}</div>
                            </td>

                            {/* Login */}
                            <td className="px-3 py-3">
                              <div
                                className={`font-mono text-[13px] font-extrabold ${
                                  row.login_time ? "text-[#10b981]" : "text-[var(--text-muted)]"
                                }`}
                              >
                                {fmtTime(row.login_time)}
                              </div>
                            </td>

                            {/* Logout */}
                            <td className="px-3 py-3">
                              <div
                                className={`font-mono text-[13px] font-extrabold ${
                                  row.logout_time ? "text-[#6366f1]" : "text-[var(--text-muted)]"
                                }`}
                              >
                                {fmtTime(row.logout_time)}
                              </div>
                            </td>

                            {/* Selfie & Campus */}
                            <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                              <SelfieCountCell row={row} onOpenViewer={openViewer} />
                            </td>

                            {/* Attendance */}
                            <td className="px-3 py-3">
                              <AttBadge desc={row.attendance_desc} />
                            </td>

                            {/* Source */}
                            <td className="px-3 py-3">
                              <SourceBadge source={row.att_source} />
                            </td>

                            {/* Payroll hours */}
                            <td className="px-3 py-3">
                              <div
                                className={`font-mono text-[14px] font-extrabold ${
                                  row.payroll_hours > 0 ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]"
                                }`}
                              >
                                {row.payroll_hours_fmt}
                              </div>
                              {row.ot_hrs > 0 && (
                                <div className="text-[#8b5cf6] text-[9px] font-extrabold">OT: {row.ot_hrs}h</div>
                              )}
                            </td>

                            {/* Reason */}
                            <td className="px-3 py-3 max-w-[160px]">
                              {row.reason ? (
                                <div className="flex flex-col gap-1">
                                  <span className="px-1.5 py-0.5 rounded bg-[rgba(251,146,60,0.12)] text-[#fb923c] text-[9px] font-extrabold w-fit">
                                    ✏ Manual
                                  </span>
                                  <span className="text-[var(--text-muted)] text-[11px] leading-snug line-clamp-2" title={row.reason}>
                                    {row.reason}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[var(--text-muted)] text-[13px]">—</span>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="relative" ref={openMenu === rowKey ? menuRef : null}>
                                <button
                                  onClick={() => setOpenMenu(openMenu === rowKey ? null : rowKey)}
                                  className="p-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-md text-[var(--text-muted)] hover:bg-[var(--surface)]"
                                >
                                  <MoreVertical size={15} />
                                </button>
                                {openMenu === rowKey && (
                                  <div className="absolute right-0 top-[calc(100%+4px)] z-50 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl shadow-lg min-w-[175px] overflow-hidden">
                                    {[
                                      {
                                        icon: <Camera size={13} />,
                                        label: "View Selfies",
                                        action: () => {
                                          openViewer(row);
                                          setOpenMenu(null);
                                        },
                                      },
                                      {
                                        icon: <Edit2 size={13} />,
                                        label: "Manual Override",
                                        action: () => {
                                          setOverrideRow(row);
                                          setOpenMenu(null);
                                        },
                                      },
                                    ].map(({ icon, label, action }) => (
                                      <button
                                        key={label}
                                        onClick={action}
                                        className="flex items-center gap-2.5 px-4 py-2.5 border-b border-[var(--divider)] last:border-b-0 w-full text-left text-[var(--text-primary)] text-[13px] hover:bg-[var(--surface)]"
                                      >
                                        <span className="text-[var(--text-muted)]">{icon}</span> {label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Expanded row */}
                          {!isFuture && isExpanded && (
                            <ExpandedRow
                              key={`${rowKey}_expanded`}
                              row={row}
                              colSpan={COL_COUNT}
                              onOpenViewer={openViewer}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="px-5 py-3.5 border-t border-[var(--divider)] flex items-center justify-between flex-wrap gap-3 bg-[var(--surface)]">
                <div className="text-[var(--text-muted)] text-[13px]">
                  Showing <strong className="text-[var(--text-primary)]">{(page - 1) * limit + 1}</strong> to{" "}
                  <strong className="text-[var(--text-primary)]">{Math.min(page * limit, total)}</strong> of{" "}
                  <strong className="text-[var(--text-primary)]">{total}</strong> entries
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page <= 1}
                    className={`px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[11px] ${
                      page <= 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--surface)]"
                    }`}
                  >
                    «
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className={`px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] flex items-center ${
                      page <= 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--surface)]"
                    }`}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pg = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                    return pg <= totalPages ? (
                      <button
                        key={pg}
                        onClick={() => setPage(pg)}
                        className={`min-w-9 px-2.5 py-1.5 rounded-xl border font-mono text-[11px] font-extrabold ${
                          page === pg
                            ? "bg-[var(--btn-bg)] border-[var(--btn-bg)] text-[var(--btn-text)]"
                            : "bg-[var(--card-bg)] border-[var(--surface-border)] text-[var(--text-muted)] hover:bg-[var(--surface)]"
                        }`}
                      >
                        {pg}
                      </button>
                    ) : null;
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className={`px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] flex items-center ${
                      page >= totalPages ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--surface)]"
                    }`}
                  >
                    <ChevronRight size={14} />
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                    className={`px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[11px] ${
                      page >= totalPages ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--surface)]"
                    }`}
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selfie Viewer Modal */}
      {viewer && (
        <SelfieViewerModal
          row={viewer.row}
          selfies={viewer.selfies}
          startIndex={viewer.startIndex || 0}
          onClose={() => setViewer(null)}
        />
      )}
    </Layout>
  );
}